/**
 * CategoryTreeSelector - Hierarchical category selector component
 * Supports infinite nesting levels
 */
import React, { useState, useEffect, useMemo } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Label } from './label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import { ScrollArea } from './scroll-area';

/**
 * Build tree structure from flat category list
 */
function buildCategoryTree(categories, parentId = null) {
  return categories
    .filter(cat => cat.parent_id === parentId)
    .map(cat => ({
      ...cat,
      children: buildCategoryTree(categories, cat.id)
    }));
}

/**
 * Get full path of a category (e.g., "Food > Drinks > Sodas")
 */
function getCategoryPath(categories, categoryId) {
  if (!categoryId) return '';
  
  const paths = [];
  let current = categories.find(c => c.id === categoryId);
  
  while (current) {
    paths.unshift(current.name);
    current = categories.find(c => c.id === current.parent_id);
  }
  
  return paths.join(' > ');
}

/**
 * TreeNode Component - Recursive node for category tree
 */
function TreeNode({ node, level = 0, selectedId, onSelect, expandedIds, onToggleExpand }) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.includes(node.id);
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div 
        className={cn(
          "flex items-center gap-1 py-1.5 px-2 rounded-md cursor-pointer hover:bg-muted transition-colors",
          isSelected && "bg-primary/10 text-primary font-medium"
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelect(node)}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(node.id);
            }}
            className="p-0.5 hover:bg-muted rounded"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}
        
        {isExpanded && hasChildren ? (
          <FolderOpen className="h-4 w-4 text-primary" />
        ) : (
          <Folder className="h-4 w-4 text-muted-foreground" />
        )}
        
        <span className="text-sm truncate">{node.name}</span>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Main CategoryTreeSelector Component
 */
export function CategoryTreeSelector({
  categories = [],
  value,
  onChange,
  placeholder = "Selecione uma categoria",
  label,
  disabled = false,
  allowClear = true,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);

  // Build tree structure
  const categoryTree = useMemo(() => buildCategoryTree(categories), [categories]);

  // Get selected category path
  const selectedPath = useMemo(() => getCategoryPath(categories, value), [categories, value]);

  // Auto-expand to show selected category
  useEffect(() => {
    if (value && categories.length > 0) {
      const idsToExpand = [];
      let current = categories.find(c => c.id === value);
      
      while (current?.parent_id) {
        idsToExpand.push(current.parent_id);
        current = categories.find(c => c.id === current.parent_id);
      }
      
      setExpandedIds(prev => [...new Set([...prev, ...idsToExpand])]);
    }
  }, [value, categories]);

  const handleSelect = (node) => {
    onChange(node.id);
    setOpen(false);
  };

  const handleToggleExpand = (id) => {
    setExpandedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className={className}>
      {label && <Label className="mb-2 block">{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal",
              !value && "text-muted-foreground"
            )}
            disabled={disabled}
          >
            <span className="truncate">
              {selectedPath || placeholder}
            </span>
            <div className="flex items-center gap-1">
              {allowClear && value && (
                <span 
                  onClick={handleClear}
                  className="hover:bg-muted rounded p-0.5"
                >
                  ×
                </span>
              )}
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <ScrollArea className="h-[300px]">
            <div className="p-2">
              {categoryTree.length === 0 ? (
                <div className="text-center text-muted-foreground py-4 text-sm">
                  Nenhuma categoria encontrada
                </div>
              ) : (
                categoryTree.map(node => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    selectedId={value}
                    onSelect={handleSelect}
                    expandedIds={expandedIds}
                    onToggleExpand={handleToggleExpand}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default CategoryTreeSelector;
